import React, { MouseEvent, useState } from 'react';

import { OperatingLeaseFinancialDetails, FinancialDetailsEditStatus } from 'cpes-common';
import { ValueOrDefaultError } from 'components/value-or-default-error';
import { TranslationFormat, useTranslationWithFormatting } from 'localization/useTranslationWithFormatting';
import { DataOverview, DefinitionListHorizontal, DefinitionListItem } from '@cp-shared-19/frontend-ui';
import { NotificationForLastEditStatus } from '../../../iban-section/NotificationForLastEditStatus';
import { SepaMandateDownload } from '../../../sepa-mandate-download/SepaMandateDownload';
import { DefaultBusinessMarketApiErrorCode, WithDefaultCpIntegrationErrors } from '@cp-shared-19/common-utilities';
import { Modal } from '@vwfs-bronson/bronson-react';

export const ConsultView: React.FC<{
    startEditing: () => void;
    details: OperatingLeaseFinancialDetails;
    lastEditStatus: FinancialDetailsEditStatus;
}> = ({ details, startEditing, lastEditStatus }) => {
    const { iban, paymentMethod, costCenter, defermentPaymentDays } = details;
    const { t, f } = useTranslationWithFormatting('financial-details');
    const [downloadError, setDownloadError] = useState<WithDefaultCpIntegrationErrors<
        DefaultBusinessMarketApiErrorCode
    > | null>(null);
    const ibanSectionFields: DefinitionListItem[] = [
        {
            label: t('ibanSection.consultView.iban.label'),
            value: <ValueOrDefaultError value={iban} />,
            tooltip: t('ibanSection.consultView.iban.tooltip'),
            labelId: t('ibanSection.consultView.iban.label'),
        },
        {
            label: t('ibanSection.consultView.paidMethod'),
            value: paymentMethod,
        },
        {
            label: t('ibanSection.consultView.costCenter.label'),
            value: costCenter,
            tooltip: t('ibanSection.consultView.costCenter.tooltip'),
        },
        {
            label: t('ibanSection.consultView.postPaymentDays'),
            value: f(defermentPaymentDays, TranslationFormat.DAYS),
        },
    ].filter(item => !!item.value);
    const handleDownloadError = (
        flag: WithDefaultCpIntegrationErrors<DefaultBusinessMarketApiErrorCode> | null,
    ): void => setDownloadError(flag);
    return (
        <DataOverview
            title={t('ibanSection.consultView.title')}
            titleLevel={5}
            buttonLabel={(details?._links?.changeIban && t('translation:editableSectionNav.start')) || ''}
            href={details?._links?.changeIban || '#'}
            buttonProps={{
                onClick: (e: MouseEvent) => {
                    e.preventDefault();
                    startEditing();
                },
                testId: 'editButton',
                tabIndex: 0,
            }}
        >
            <DefinitionListHorizontal list={ibanSectionFields} />
            {details?._links?.sepaMandate && (
                <SepaMandateDownload
                    downloadLinkLabel={t('ibanSection.consultView.sepa-mandate')}
                    sepaMandateDownloadEndpoint={details._links.sepaMandate}
                    handleDownloadError={handleDownloadError}
                />
            )}
            <NotificationForLastEditStatus lastEditStatus={lastEditStatus} forOperatingLease />
            <Modal
                shown={!!downloadError}
                buttonConfirmText={t('ibanSection.editView.sepa-mandate-view.download-failure-modal.accept-button')}
                onConfirm={(): void => handleDownloadError(null)}
                onClose={(): void => handleDownloadError(null)}
                title={t('ibanSection.editView.sepa-mandate-view.download-failure-modal.title')}
                status="error"
                testId="downloadFailureModal"
            >
                {t('ibanSection.editView.sepa-mandate-view.download-failure-modal.text')}
            </Modal>
        </DataOverview>
    );
};
